/**
 * useAiJob — V1.8.9
 *
 * Backend endpoints that used to block on Claude/Whisper now return a
 * `{job_id, status: "pending"}` shape. This hook wraps the polling loop
 * so page components stay declarative.
 *
 *   const { data, error, pending, run } = useAiJob();
 *   run({url: "/interview/questions", body: {...}})
 *     → hits the URL, if the response has `job_id` polls
 *       /api/ai-jobs/{id} every 2 s until status ∈ {done, error};
 *       otherwise treats the response itself as the final result
 *       (backward-compatible with cache hits and legacy endpoints).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 90;  // 90 * 2s = 3 min ceiling, matches backend JOB_MAX_RUNTIME_SEC.

/**
 * Imperative helper — poll an AI job until it's done. Throws on error.
 * Handy for one-shot flows where the useAiJob hook doesn't fit
 * (e.g. multiple independent call sites in one component).
 */
export async function pollAiJob(jobId, { intervalMs = POLL_INTERVAL_MS, maxPolls = MAX_POLLS } = {}) {
    for (let i = 0; i < maxPolls; i++) {
        const res = await api.get(`/ai-jobs/${jobId}`);
        const doc = res?.data;
        if (!doc) throw new Error("Empty job response");
        if (doc.status === "done") return doc.result || {};
        if (doc.status === "error") throw new Error(doc.error || "AI job failed");
        await new Promise((r) => setTimeout(r, intervalMs));
    }
    throw new Error("This is taking longer than expected. Please retry.");
}

/**
 * If the response has a job_id, poll; otherwise return the payload
 * unchanged (cache hits & pre-V1.8.9 endpoints).
 */
export async function resolveAiResponse(responseData) {
    if (
        responseData
        && responseData.job_id
        && (responseData.status === "pending" || responseData.status === "running")
    ) {
        return pollAiJob(responseData.job_id);
    }
    return responseData;
}

export default function useAiJob() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(false);
    const [jobId, setJobId] = useState(null);
    const cancelRef = useRef(false);
    const timerRef = useRef(null);

    const stopPolling = useCallback(() => {
        cancelRef.current = true;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => () => stopPolling(), [stopPolling]);

    const pollUntilDone = useCallback(async (id) => {
        try {
            const result = await pollAiJob(id, {
                intervalMs: POLL_INTERVAL_MS,
                maxPolls: MAX_POLLS,
            });
            if (cancelRef.current) return;
            setData(result);
            setPending(false);
        } catch (err) {
            if (cancelRef.current) return;
            setError(err?.message || "AI job failed. Please retry.");
            setPending(false);
        }
    }, []);

    const run = useCallback(async ({ url, body, method = "post", isFormData = false }) => {
        setData(null);
        setError(null);
        setPending(true);
        setJobId(null);
        cancelRef.current = false;
        try {
            const cfg = isFormData
                ? { headers: { "Content-Type": "multipart/form-data" } }
                : undefined;
            const res = method === "get"
                ? await api.get(url, cfg)
                : await api.post(url, body, cfg);
            const payload = res?.data || {};
            if (payload.job_id && (payload.status === "pending" || payload.status === "running")) {
                setJobId(payload.job_id);
                await pollUntilDone(payload.job_id);
            } else {
                // Cache hit / legacy sync response — done immediately.
                setData(payload);
                setPending(false);
            }
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || "Request failed.");
            setPending(false);
        }
    }, [pollUntilDone]);

    const reset = useCallback(() => {
        stopPolling();
        setData(null);
        setError(null);
        setPending(false);
        setJobId(null);
    }, [stopPolling]);

    return { data, error, pending, jobId, run, reset };
}
